import type { FormMessageOverrides } from './schemas';
import type { FormFieldTextEntry, FormFieldTextOverrides } from './types';

/**
 * Maps the repeatable `shared.form-field-text` Strapi component into the
 * per-field overrides record `ContactForm` consumes. Later entries for the
 * same field win (mirrors how editors would expect a duplicate row to
 * behave); empty strings are treated as "not set" so an accidentally
 * blank CMS field never blanks out a label.
 */
export function toFieldTextOverrides(
  entries: FormFieldTextEntry[] | undefined,
): FormFieldTextOverrides | undefined {
  if (!entries?.length) {
    return undefined;
  }
  const overrides: FormFieldTextOverrides = {};
  for (const entry of entries) {
    if (!entry?.field) {
      continue;
    }
    overrides[entry.field] = {
      ...overrides[entry.field],
      ...(entry.label ? { label: entry.label } : {}),
      ...(entry.validationErrorText
        ? { validationErrorText: entry.validationErrorText }
        : {}),
    };
  }
  return overrides;
}

/**
 * Extracts the per-field validation-message overrides from `fieldTexts`
 * for `createFormSchemas`.
 */
export function toMessageOverrides(
  fieldTexts: FormFieldTextOverrides | undefined,
): FormMessageOverrides {
  if (!fieldTexts) {
    return {};
  }
  const messages: FormMessageOverrides = {};
  for (const field of [
    'salutation',
    'firstname',
    'lastname',
    'phone',
    'email',
  ] as const) {
    const text = fieldTexts[field]?.validationErrorText;
    if (text) {
      messages[field] = text;
    }
  }
  return messages;
}
